import {
  IonSpinner,
  IonGrid,
  IonFabButton,
  IonIcon,
  IonAlert,
  IonLoading
} from '@ionic/react';
import { trashOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Patient } from '../models';
import PatientRepository from '../repositories/PatientRepository';
import EventBus from '../services/EventBus';
import { AuthenticationRootState } from '../store/reducers/authentication';

interface Props {
  allowDelete?: boolean;
  onSelectPatient?: (patient: Patient) => any;
  lastUpdate?: number;
  className?: string;
  defaultPatient?: Patient;
}

const mapStateToProps = (
  state: { authentication: AuthenticationRootState },
  ownProps: any
) => {
  return {
    user: state.authentication.user!
  };
};

const PatientSwitcher: React.FC<Props & ReturnType<typeof mapStateToProps>> = ({
  allowDelete,
  onSelectPatient = () => {},
  user,
  lastUpdate,
  className = '',
  defaultPatient
}) => {
  const [isPatientsLoading, setIsPatientsLoading] = useState(false);
  const [availablePatients, setAvailablePatients] = useState<Patient[]>([]);
  const [currentEditionPatient, setCurrentEditionPatient] =
    useState<Patient | null>(defaultPatient ?? null);
  const [showDeletePatientModal, setShowDeletePatientModal] = useState(false);
  const [deletePatientLoading, setDeletePatientLoading] = useState(false);

  useEffect(() => {
    if (currentEditionPatient) {
      onSelectPatient(currentEditionPatient);
    }
  }, [currentEditionPatient, onSelectPatient]);

  async function deletePatient(patient: Patient) {
    if (patient.id !== user.patient.id) {
      setDeletePatientLoading(true);
      if (await PatientRepository.delete(patient)) {
        EventBus.success(
          `Le patient ${patient.getFullName()} a bien été supprimé.`
        );
      } else {
        EventBus.error(
          'Erreur lors de la suppression du patient. Veuillez essayer de nouveau.'
        );
      }

      setDeletePatientLoading(false);
      onLoad();
    }
  }

  const onLoad = () => {
    let isRendered = true;

    async function fetchPatient() {
      if (user?.id) {
        setIsPatientsLoading(true);
        const allPatient = await PatientRepository.findAll();
        setAvailablePatients(
          allPatient.filter((patient) => patient.id !== user?.patient.id)
        );
        if (isRendered) {
          setIsPatientsLoading(false);
        }
      }
    }

    fetchPatient();

    return () => {
      isRendered = false;
    };
  };

  useEffect(onLoad, [user, lastUpdate]);

  if (isPatientsLoading) {
    return (
      <div className={`other-patients ${className}`}>
        <IonSpinner name="dots" />
      </div>
    );
  } else {
    return (
      <>
        <IonGrid
          className={`other-patients ion-text-center ion-justify-content-start ${className}`}
        >
          {[user.patient, ...availablePatients].map((patient) => (
            <div
              key={patient.id}
              className="ion-margin-horizontal"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <div style={{ position: 'relative' }}>
                {allowDelete &&
                patient.id !== user.patient.id &&
                patient.id === currentEditionPatient?.id ? (
                  <IonFabButton
                    className="x-small light"
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      zIndex: 10,
                      transform: 'translateX(70%) translateY(-20%)'
                    }}
                    onClick={() => {
                      setCurrentEditionPatient(patient);
                      setShowDeletePatientModal(true);
                    }}
                  >
                    <IonIcon icon={trashOutline} />
                  </IonFabButton>
                ) : null}
                <IonFabButton
                  className={`${
                    patient.id !== currentEditionPatient?.id ? 'info' : ''
                  } initials`}
                  onClick={() => {
                    setCurrentEditionPatient(patient);
                  }}
                >
                  {patient.getInitials()}
                </IonFabButton>
              </div>
              <div
                style={{ marginTop: '5px' }}
                onClick={() => {
                  setCurrentEditionPatient(patient);
                }}
              >
                {patient.id === currentEditionPatient?.id ? (
                  <strong>{patient.getFullName()}</strong>
                ) : (
                  <span>{patient.getFullName()}</span>
                )}
              </div>
            </div>
          ))}
        </IonGrid>
        {allowDelete ? (
          <>
            <IonLoading isOpen={deletePatientLoading} />
            <IonAlert
              isOpen={showDeletePatientModal}
              onDidDismiss={() => setShowDeletePatientModal(false)}
              cssClass="my-custom-class"
              header={`Êtes-vous certain de vouloir supprimer ce patient ?`}
              subHeader={currentEditionPatient?.getFullName()}
              buttons={[
                {
                  text: 'Cancel',
                  role: 'cancel',
                  cssClass: 'secondary'
                },
                {
                  text: 'Ok',
                  handler: () => {
                    if (currentEditionPatient) {
                      deletePatient(currentEditionPatient);
                    }
                  }
                }
              ]}
            />
          </>
        ) : null}
      </>
    );
  }
};

export default connect(mapStateToProps, null)(PatientSwitcher);
